import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getDateLocale } from '../i18n';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { getImputations, getCurrentUserProfile, getEmployeeTeamStats, getTeams, getUsers, getMyTeamMembers, getManagerDashboardStats, getTeamOperationsByPeriod, getTenantSubdomain } from '../services/api';
import { RS_CHART_PERF_USER } from '../utils/rsChartPalette';
import { getCompletedEtatCode, RS_ETAT_CODES } from '../utils/imputationEtats';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const EmployeePerformanceComparisonPage = () => {
  const { t } = useTranslation('employee');
  const { t: tCommon } = useTranslation('common');
  const tenantSub = getTenantSubdomain();
  const isRsPerf = (tenantSub || '').toLowerCase() === 'rs';
  const completedEtat = getCompletedEtatCode(tenantSub);
  const failedEtat = isRsPerf ? 'FINAL' : 'ECHEC';
  const pendingEtat = isRsPerf ? 'TO_DO' : 'EN_ATTENTE';
  const isWorkedHoursStatus = (etat) =>
    isRsPerf ? RS_ETAT_CODES.includes(etat) : etat === 'TERMINE' || etat === 'ECHEC';

  // State for operations stats
  const [operationsStats, setOperationsStats] = useState({
    today: 0,
    week: 0,
    month: 0,
    loading: true
  });

  // State for worked hours (today)
  const [workedHours, setWorkedHours] = useState({
    hours: 0,
    loading: true
  });

  // Daily objective (hours) from employee's centre – dynamic per centre, default 7
  const [dailyObjectiveHours, setDailyObjectiveHours] = useState(7);

  // State for team positioning
  const [teamPositioning, setTeamPositioning] = useState({
    rank: null,
    totalTeamMembers: 0,
    teamName: '',
    yourAvg: 0,
    teamAvg: 0,
    yourPercentage: 0,
    teamPercentage: 0,
    loading: true,
    // Rankings for different metrics
    rankings: {
      operations: { rank: null, value: 0 },
      imputations: { rank: null, value: 0 },
      hours: { rank: null, value: 0 }
    }
  });

  // State for selected ranking metric tab
  const [selectedRankingMetric, setSelectedRankingMetric] = useState('operations');

  // State for selected graph metric tab
  const [selectedGraphMetric, setSelectedGraphMetric] = useState('operations');

  // State for quality score
  const [qualityScore, setQualityScore] = useState({
    score: 0,
    completed: 0,
    failed: 0,
    pending: 0,
    cancelled: 0,
    total: 0,
    loading: true
  });

  // State for chart data
  const [chartData, setChartData] = useState({
    labels: [],
    userData: [], // For backward compatibility (defaults to operations)
    teamAvgData: [], // For backward compatibility (defaults to operations)
    loading: true,
    // Data for different metrics
    operations: { userData: [], teamAvgData: [] },
    imputations: { userData: [], teamAvgData: [] },
    hours: { userData: [], teamAvgData: [] }
  });

  // State for activity log
  const [activityLog, setActivityLog] = useState({
    data: [],
    loading: true,
    total: 0
  });

  // State for activity log pagination
  const [activityLogPage, setActivityLogPage] = useState(1);
  const [activityLogPageSize, setActivityLogPageSize] = useState(20);
  const [operationNameFilter, setOperationNameFilter] = useState('');

  // State for filters
  const [selectedTeam, setSelectedTeam] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState('Today');
  const [customDateRange, setCustomDateRange] = useState({
    startDate: '',
    endDate: ''
  });
  const [availableTeams, setAvailableTeams] = useState([]);
  const [loadingTeams, setLoadingTeams] = useState(false);

  // Calculate date ranges
  const getDateRanges = () => {
    const today = new Date();
    
    // Format date as YYYY-MM-DD using local timezone (not UTC to avoid date shifts)
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    // Start of today (current date)
    const todayDateStr = formatDate(today);

    // Start of month (first day of current month)
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const startOfMonthStr = formatDate(startOfMonth);

    // Start of week (Monday as start of week)
    const startOfWeek = new Date(today);
    const dayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    // Calculate days to subtract to get to Monday
    // If Sunday (0), go back 6 days; otherwise go back (dayOfWeek - 1) days
    const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    startOfWeek.setDate(today.getDate() - daysToSubtract);
    startOfWeek.setHours(0, 0, 0, 0); // Reset to start of day
    const startOfWeekStr = formatDate(startOfWeek);

    const dateRanges = {
      today: todayDateStr,
      week: startOfWeekStr,
      month: startOfMonthStr,
      todayEnd: todayDateStr
    };

    return dateRanges;
  };

  // Calculate filtered date range based on period selection
  const getFilteredDateRange = (period, customRange) => {
    const today = new Date();
    
    // Format date as YYYY-MM-DD using local timezone
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    let dateFrom, dateTo;

    switch (period) {
      case 'Today':
        dateFrom = formatDate(today);
        dateTo = formatDate(today);
        break;
      
      case 'This Week':
        const startOfWeek = new Date(today);
        const dayOfWeek = today.getDay();
        const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        startOfWeek.setDate(today.getDate() - daysToSubtract);
        startOfWeek.setHours(0, 0, 0, 0);
        dateFrom = formatDate(startOfWeek);
        dateTo = formatDate(today);
        break;
      
      case 'This Month':
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        dateFrom = formatDate(startOfMonth);
        dateTo = formatDate(today);
        break;
      
      case 'Last Month':
        const firstDayLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const lastDayLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
        dateFrom = formatDate(firstDayLastMonth);
        dateTo = formatDate(lastDayLastMonth);
        break;
      
      case 'Last 3 Months':
        const threeMonthsAgo = new Date(today.getFullYear(), today.getMonth() - 3, 1);
        dateFrom = formatDate(threeMonthsAgo);
        dateTo = formatDate(today);
        break;
      
      case 'Custom Range':
        dateFrom = customRange.startDate || formatDate(today);
        dateTo = customRange.endDate || formatDate(today);
        break;
      
      default:
        // Default to This Month
        const defaultStartOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        dateFrom = formatDate(defaultStartOfMonth);
        dateTo = formatDate(today);
    }

    return { date_from: dateFrom, date_to: dateTo };
  };

  // Fetch user's team and set as default
  useEffect(() => {
    const fetchUserTeam = async () => {
      try {
        setLoadingTeams(true);
        
        // Get user's team from profile
          const userProfile = await getCurrentUserProfile();
          if (userProfile?.team) {
            setAvailableTeams([userProfile.team]);
            setSelectedTeam(userProfile.team.id.toString());
          } else {
            setAvailableTeams([]);
          }
      } catch (error) {
        console.error('Error fetching user team:', error);
          setAvailableTeams([]);
      } finally {
        setLoadingTeams(false);
      }
    };

    fetchUserTeam();
  }, []);

  // Helper function to convert temps_standard to hours
  const convertTempsStandardToHours = (tempsStandard) => {
    if (!tempsStandard) return 0;
    
    // Handle string format "HH:MM"
    if (typeof tempsStandard === 'string' && tempsStandard.includes(':')) {
      const [hours, minutes] = tempsStandard.split(':').map(Number);
      return hours + (minutes / 60);
    }
    
    // Handle decimal format (already in hours)
    if (typeof tempsStandard === 'number') {
      return tempsStandard;
    }
    
    // Handle string number (assume minutes)
    const numValue = parseFloat(tempsStandard);
    if (!isNaN(numValue)) {
      // If value is less than 24, assume it's hours; otherwise assume minutes
      return numValue < 24 ? numValue : numValue / 60;
    }
    
    return 0;
  };

  // Helper function to format decimal hours to "Xh Ym" format
  const formatHoursAndMinutes = (decimalHours) => {
    if (!decimalHours || decimalHours === 0) return '0h 0m';
    const hours = Math.floor(decimalHours);
    const minutes = Math.round((decimalHours - hours) * 60);
    if (minutes === 0) {
      return `${hours}h`;
    }
    return `${hours}h ${minutes}m`;
  };

  // Helper function to format decimal hours to "HH:MM" format for Y-axis
  const formatHoursToHHMM = (decimalHours) => {
    if (!decimalHours || decimalHours === 0) return '00:00';
    const hours = Math.floor(decimalHours);
    const minutes = Math.round((decimalHours - hours) * 60);
    const hoursStr = String(hours).padStart(2, '0');
    const minutesStr = String(minutes).padStart(2, '0');
    return `${hoursStr}:${minutesStr}`;
  };

  // Load daily objective from the user's centre (profile.centre = centre the user belongs to)
  useEffect(() => {
    const loadDailyObjective = async () => {
      try {
        const profile = await getCurrentUserProfile();
        const centre = profile?.centre; // User's centre (from profile API)
        const h = centre?.daily_objective_hours != null ? Number(centre.daily_objective_hours) : 7;
        const m = centre?.daily_objective_minutes != null ? Number(centre.daily_objective_minutes) : 0;
        const totalDecimal = Math.min(24, h + m / 60);
        if (totalDecimal > 0) {
          setDailyObjectiveHours(totalDecimal);
        }
      } catch (err) {
        console.error('Error loading daily objective from centre:', err);
      }
    };
    loadDailyObjective();
  }, []);

  // Fetch operations stats for this user
  useEffect(() => {
    const fetchOperationsStats = async () => {
      try {
        setOperationsStats(prev => ({ ...prev, loading: true }));
        setWorkedHours(prev => ({ ...prev, loading: true }));
        const dateRanges = getDateRanges();

        // Fetch today's imputations (all statuses) for worked hours; week/month TERMINE for counts
        const [todayData, weekData, monthData] = await Promise.all([
          getImputations({
            date_from: dateRanges.today,
            date_to: dateRanges.todayEnd
          }, 1, 1000),
          getImputations({
            date_from: dateRanges.week,
            date_to: dateRanges.todayEnd,
            etat_final: completedEtat
          }, 1, 1),
          getImputations({
            date_from: dateRanges.month,
            date_to: dateRanges.todayEnd,
            etat_final: completedEtat
          }, 1, 1)
        ]);

        // Worked hours: sum imputation.temps_standard (Failed=50%, Completed=100%)
        const todayImputations = todayData?.results || [];
        let totalHours = 0;
        todayImputations.forEach(imputation => {
          const workedTime = imputation.temps_standard ?? imputation.operation?.temps_standard;
          totalHours += convertTempsStandardToHours(workedTime);
        });

        setOperationsStats({
          today: todayImputations.filter(i => i.etat_final === completedEtat).length,
          week: weekData?.count || 0,
          month: monthData?.count || 0,
          loading: false
        });

        setWorkedHours({
          hours: parseFloat(totalHours.toFixed(2)),
          loading: false
        });
      } catch (error) {
        console.error('Error fetching operations stats:', error);
        setOperationsStats(prev => ({ ...prev, loading: false }));
        setWorkedHours(prev => ({ ...prev, loading: false }));
      }
    };

    fetchOperationsStats();
  }, []);

  // Fetch team positioning data
  useEffect(() => {
    const fetchTeamPositioning = async () => {
      try {
        setTeamPositioning(prev => ({ ...prev, loading: true }));
        const userProfile = await getCurrentUserProfile();
        
        // Use selected team or fallback to user's team
        const teamId = selectedTeam ? parseInt(selectedTeam) : (userProfile?.team?.id);
        const selectedTeamData = availableTeams.find(t => t.id.toString() === selectedTeam);
        const teamName = selectedTeamData?.name || userProfile?.team?.name || t('performance.yourTeam');

        if (!teamId) {
          setTeamPositioning(prev => ({ ...prev, loading: false }));
          return;
        }

        // Get filtered date range based on selected period
        const dateRange = getFilteredDateRange(selectedPeriod, customDateRange);
        
        // Fetch current user's successful operations for the filtered period
        const userImputations = await getImputations({
          date_from: dateRange.date_from,
          date_to: dateRange.date_to,
          etat_final: completedEtat
        }, 1, 1000);

        const userSuccessfulOps = userImputations?.count || 0;
        const userResults = userImputations?.results || [];
        
        // Helper function to calculate working days and hours for a date range
        const calculateWorkingDaysAndHours = (dateFrom, dateTo) => {
          const startDate = new Date(dateFrom);
          const endDate = new Date(dateTo);
        let workingDays = 0;
        let currentDate = new Date(startDate);
        
        while (currentDate <= endDate) {
          const dayOfWeek = currentDate.getDay();
          // Count Monday to Friday (1-5)
          if (dayOfWeek >= 1 && dayOfWeek <= 5) {
            workingDays++;
          }
          currentDate.setDate(currentDate.getDate() + 1);
        }
        
          const workingHours = workingDays * 7; // 7 hours per day
          return { workingDays, workingHours };
        };

        // Calculate user's average operations per hour
        const { workingDays, workingHours } = calculateWorkingDaysAndHours(dateRange.date_from, dateRange.date_to);
        
        // Handle edge case: if no working days, set avg to 0
        let yourAvgOpsPerHour = 0;
        if (workingHours > 0) {
          yourAvgOpsPerHour = parseFloat((userSuccessfulOps / workingHours).toFixed(1));
        }

        // Calculate real team average from team members' data using the API endpoint
        let teamAvgOpsPerHour = null; // Use null to track if API call succeeded
        let apiCallSucceeded = false;
        
        try {
          // Use the employee team stats API endpoint which calculates team average correctly
          // Team avg = sum of (each team member's avg ops per hour) / count of team members
          const teamStats = await getEmployeeTeamStats({
            date_from: dateRange.date_from,
            date_to: dateRange.date_to
          });
          
          apiCallSucceeded = true;
          
          // #region agent log
          // fetch('http://127.0.0.1:7247/ingest/69caa0a3-7f19-4041-86d8-9ea723023a00',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'EmployeePerformanceComparisonPage.js:fetchTeamPositioning','message':'Team stats API response received','data':{teamStats,hasTeamAvg:teamStats?.team_avg_ops_per_hour !== undefined,teamAvg:teamStats?.team_avg_ops_per_hour,teamAvgType:typeof teamStats?.team_avg_ops_per_hour},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'H'})}).catch(()=>{});
          // #endregion
          
          // Check if team_avg_ops_per_hour is defined (including 0, which is a valid value)
          if (teamStats && teamStats.team_avg_ops_per_hour !== undefined && teamStats.team_avg_ops_per_hour !== null) {
            // Parse the value - 0 is a valid team average, don't treat it as missing
            const parsedTeamAvg = parseFloat(teamStats.team_avg_ops_per_hour);
            // Only use the parsed value if it's a valid number (not NaN)
            if (!isNaN(parsedTeamAvg)) {
              teamAvgOpsPerHour = parsedTeamAvg;
            }
            // #region agent log
            // fetch('http://127.0.0.1:7247/ingest/69caa0a3-7f19-4041-86d8-9ea723023a00',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'EmployeePerformanceComparisonPage.js:fetchTeamPositioning','message':'Team avg parsed from API','data':{parsedTeamAvg,teamAvgOpsPerHour,originalValue:teamStats.team_avg_ops_per_hour},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'H1'})}).catch(()=>{});
            // #endregion
          }
        } catch (teamStatsError) {
          // #region agent log
          // fetch('http://127.0.0.1:7247/ingest/69caa0a3-7f19-4041-86d8-9ea723023a00',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'EmployeePerformanceComparisonPage.js:fetchTeamPositioning','message':'Team stats API error','data':{error:teamStatsError?.message || String(teamStatsError),status:teamStatsError?.status},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'I'})}).catch(()=>{});
          // #endregion
          
          console.warn('Error fetching team stats from API, using fallback:', teamStatsError);
          
          // Fallback: if API fails, try manager dashboard stats if user is a manager
          try {
            const managerStats = await getManagerDashboardStats({
              date_from: dateRange.date_from,
              date_to: dateRange.date_to,
              team: teamId.toString()
            });
            
            if (managerStats?.kpis?.total_operations !== undefined) {
              const teamTotalOps = managerStats.kpis.total_operations || 0;
              let teamMemberCount = 0;
              
              if (managerStats.top_employees && Array.isArray(managerStats.top_employees)) {
                teamMemberCount = managerStats.top_employees.length;
              }
              
              if (workingHours > 0 && teamMemberCount > 0) {
                teamAvgOpsPerHour = parseFloat((teamTotalOps / teamMemberCount / workingHours).toFixed(1));
                apiCallSucceeded = true; // Manager stats worked
              }
            }
          } catch (managerStatsError) {
            // API call failed, will use fallback below
            apiCallSucceeded = false;
          }
        }

        // Only use fallback if the API call failed (apiCallSucceeded is false)
        // If API succeeded but returned 0, that's a valid team average (team has no operations)
        if (!apiCallSucceeded && teamAvgOpsPerHour === null) {
          // Final fallback: use user's avg as team avg only if API completely failed
          teamAvgOpsPerHour = yourAvgOpsPerHour;
          // #region agent log
          // fetch('http://127.0.0.1:7247/ingest/69caa0a3-7f19-4041-86d8-9ea723023a00',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'EmployeePerformanceComparisonPage.js:fetchTeamPositioning','message':'Using fallback - API failed','data':{teamAvgOpsPerHour,yourAvgOpsPerHour},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'J2'})}).catch(()=>{});
          // #endregion
        } else if (teamAvgOpsPerHour === null) {
          // API succeeded but didn't return a value - set to 0
          teamAvgOpsPerHour = 0;
        }
        
        // #region agent log
        // fetch('http://127.0.0.1:7247/ingest/69caa0a3-7f19-4041-86d8-9ea723023a00',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'EmployeePerformanceComparisonPage.js:fetchTeamPositioning','message':'Final team avg calculated','data':{teamAvgOpsPerHour,yourAvgOpsPerHour,workingHours,apiCallSucceeded},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'J'})}).catch(()=>{});
        // #endregion
        
        // #region agent log
        // fetch('http://127.0.0.1:7247/ingest/69caa0a3-7f19-4041-86d8-9ea723023a00',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'EmployeePerformanceComparisonPage.js:fetchTeamPositioning','message':'Final team avg calculated','data':{teamAvgOpsPerHour,yourAvgOpsPerHour,workingHours},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'J'})}).catch(()=>{});
        // #endregion

        const userOpsPerHour = yourAvgOpsPerHour;

        // Calculate team rankings for all metrics
        let rank = null;
        let totalTeamMembers = 0;
        const rankings = {
          operations: { rank: null, value: 0 },
          imputations: { rank: null, value: 0 },
          hours: { rank: null, value: 0 }
        };
        
        try {
          // Get current user profile to identify current user
          const currentUserProfile = await getCurrentUserProfile();
          const currentUserId = currentUserProfile?.id;
          
          // Get team operations (excludes current user)
          const teamOpsResponse = await getTeamOperationsByPeriod({
            date_from: dateRange.date_from,
            date_to: dateRange.date_to
          });
          
          const teamOperations = teamOpsResponse?.results || [];
          totalTeamMembers = teamOpsResponse?.team_member_count || 0;
          
          // Group team operations by employee and calculate metrics
          const memberStats = {};
          
          teamOperations.forEach(op => {
            // Handle different employee data structures
            let employeeId = null;
            if (op.employe) {
              employeeId = typeof op.employe === 'object' ? (op.employe.id || op.employe.user?.id) : op.employe;
            } else if (op.employee) {
              employeeId = typeof op.employee === 'object' ? (op.employee.id || op.employee.user?.id) : op.employee;
            } else if (op.employe_id) {
              employeeId = op.employe_id;
            }
            
            if (employeeId) {
              if (!memberStats[employeeId]) {
                memberStats[employeeId] = {
                  id: employeeId,
                  operations: 0, // Count of successful operations (TERMINE)
                  imputations: 0, // Total count of all imputations
                  hours: 0 // Total hours based on standard time
                };
              }
              
              // Count all imputations
              memberStats[employeeId].imputations++;
              
              // Count successful operations
              if (op.etat_final === completedEtat) {
                memberStats[employeeId].operations++;
                
                // Calculate hours from standard time
                const standardTime = op.operation?.temps_standard || op.temps_standard;
                const hours = convertTempsStandardToHours(standardTime);
                memberStats[employeeId].hours += hours;
              }
            }
          });
          
          // Get current user's imputations to calculate their metrics
          const userAllImputations = await getImputations({
            date_from: dateRange.date_from,
            date_to: dateRange.date_to
          }, 1, 1000);
          
          const userImputationsList = userAllImputations?.results || [];
          let userOperations = 0;
          let userImputations = userImputationsList.length;
          let userHours = 0;
          
          userImputationsList.forEach(imp => {
            if (imp.etat_final === completedEtat) {
              userOperations++;
            }
            if (isWorkedHoursStatus(imp.etat_final)) {
              const workedTime = imp.temps_standard ?? imp.operation?.temps_standard;
              userHours += convertTempsStandardToHours(workedTime);
            }
          });
          
          // Add current user's stats
          memberStats[currentUserId] = {
            id: currentUserId,
            operations: userOperations,
            imputations: userImputations,
            hours: userHours
          };
          
          // Get team members count (including current user)
          const teamMembers = await getMyTeamMembers();
          totalTeamMembers = (teamMembers?.length || 0) + 1; // +1 for current user
          
          // Calculate rankings for each metric
          const metrics = ['operations', 'imputations', 'hours'];
          
          metrics.forEach(metric => {
            // Convert to array and sort by metric descending
            const sortedStats = Object.values(memberStats)
              .map(member => ({
                id: member.id,
                value: member[metric]
              }))
              .sort((a, b) => b.value - a.value);
            
            // Find current user's rank (1-based)
            const currentUserIndex = sortedStats.findIndex(m => m.id === currentUserId);
            if (currentUserIndex !== -1) {
              rankings[metric].rank = currentUserIndex + 1;
              rankings[metric].value = sortedStats[currentUserIndex].value;
            }
          });
          
          // Set default rank to operations rank for backward compatibility
          rank = rankings.operations.rank;
        } catch (rankingError) {
          console.warn('Error calculating team ranking:', rankingError);
          // If ranking fails, try to get team member count at least
          try {
            const teamMembers = await getMyTeamMembers();
            totalTeamMembers = (teamMembers?.length || 0) + 1; // +1 for current user
          } catch (error) {
            console.warn('Error getting team members:', error);
          }
        }

        // Calculate progress bar percentages (relative comparison)
        const maxOpsPerHour = Math.max(userOpsPerHour, parseFloat(teamAvgOpsPerHour)) * 1.3;
        const yourPercentage = maxOpsPerHour > 0 ? (userOpsPerHour / maxOpsPerHour) * 100 : 0;
        const teamPercentage = maxOpsPerHour > 0 ? (parseFloat(teamAvgOpsPerHour) / maxOpsPerHour) * 100 : 0;

        setTeamPositioning({
          rank,
          totalTeamMembers,
          teamName,
          yourAvg: parseFloat(yourAvgOpsPerHour),
          teamAvg: parseFloat(teamAvgOpsPerHour),
          yourPercentage: Math.round(yourPercentage),
          teamPercentage: Math.round(teamPercentage),
          rankings,
          loading: false
        });
      } catch (error) {
        console.error('Error fetching team positioning:', error);
        setTeamPositioning(prev => ({ ...prev, loading: false }));
      }
    };

    fetchTeamPositioning();
  }, [selectedTeam, selectedPeriod, customDateRange, availableTeams]);

  // Fetch quality score data
  useEffect(() => {
    const fetchQualityScore = async () => {
      try {
        setQualityScore(prev => ({ ...prev, loading: true }));
        
        // Get filtered date range based on selected period
        const dateRange = getFilteredDateRange(selectedPeriod, customDateRange);
        
        // Fetch all imputations for the filtered period (to calculate quality metrics)
        const allImputations = await getImputations({
          date_from: dateRange.date_from,
          date_to: dateRange.date_to
        }, 1, 1000);

        let results = allImputations?.results || [];
        
        // Filter by team if a specific team is selected
        // Note: Since getImputations only returns current user's imputations,
        // team filtering would require backend support or filtering team members' data
        // For now, we'll use the current user's data regardless of team selection
        // This can be enhanced later when team-wide data access is available
        
        // Calculate quality metrics for all states
        const completed = results.filter(imp => imp.etat_final === completedEtat).length;
        const failed = results.filter(imp => imp.etat_final === failedEtat).length;
        const pending = results.filter(imp => imp.etat_final === pendingEtat).length;
        const cancelled = results.filter(imp => imp.etat_final === 'ANNULE').length;
        const total = results.length;
        
        // Calculate quality score: (Completed / Total Evaluated) * 100
        // Total evaluated = completed + failed (only these count for quality score)
        const totalEvaluated = completed + failed;
        const score = totalEvaluated > 0 ? Math.round((completed / totalEvaluated) * 100) : 0;

        setQualityScore({
          score,
          completed,
          failed,
          pending,
          cancelled,
          total,
          loading: false
        });
      } catch (error) {
        console.error('Error fetching quality score:', error);
        setQualityScore(prev => ({ ...prev, loading: false }));
      }
    };

    fetchQualityScore();
  }, [selectedTeam, selectedPeriod, customDateRange]);

  // Fetch chart data based on filters
  useEffect(() => {
    const fetchChartData = async () => {
      try {
        setChartData(prev => ({ ...prev, loading: true }));
        
        // Get filtered date range
        const dateRange = getFilteredDateRange(selectedPeriod, customDateRange);
        const startDate = new Date(dateRange.date_from);
        const endDate = new Date(dateRange.date_to);
        const daysDiff = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
        
        // #region agent log
        // fetch('http://127.0.0.1:7247/ingest/69caa0a3-7f19-4041-86d8-9ea723023a00',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'EmployeePerformanceComparisonPage.js:fetchChartData','message':'Chart data fetch started','data':{selectedPeriod,dateRange,startDate:startDate.toISOString(),endDate:endDate.toISOString(),daysDiff},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A'})}).catch(()=>{});
        // #endregion
        
        // Fetch user imputations for the filtered period
        // Fetch all imputations for imputations and hours metrics
        const allImputationsResponse = await getImputations({
          date_from: dateRange.date_from,
          date_to: dateRange.date_to
        }, 1, 1000);

        const allImputations = allImputationsResponse?.results || [];
        
        // Filter for operations metric (TERMINE only)
        const imputations = allImputations.filter(imp => imp.etat_final === completedEtat);
        
        // #region agent log
        // fetch('http://127.0.0.1:7247/ingest/69caa0a3-7f19-4041-86d8-9ea723023a00',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'EmployeePerformanceComparisonPage.js:fetchChartData','message':'User imputations fetched','data':{dateRange,imputationsCount:imputations.length,imputationsSample:imputations.slice(0,3).map(imp=>({id:imp.id,date_operation:imp.date_operation,date_saisie:imp.date_saisie,etat_final:imp.etat_final}))},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A'})}).catch(()=>{});
        // #endregion
        
        // Fetch team member operations (excluding current user)
        let teamOperations = [];
        let teamMemberCount = 0;
        try {
          const teamOpsResponse = await getTeamOperationsByPeriod({
            date_from: dateRange.date_from,
            date_to: dateRange.date_to
          });
          teamOperations = teamOpsResponse?.results || [];
          teamMemberCount = teamOpsResponse?.team_member_count || 0;
          // #region agent log
          // fetch('http://127.0.0.1:7247/ingest/69caa0a3-7f19-4041-86d8-9ea723023a00',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'EmployeePerformanceComparisonPage.js:fetchChartData','message':'Team operations fetched','data':{teamOpsCount:teamOperations.length,teamMemberCount},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'B'})}).catch(()=>{});
          // #endregion
        } catch (teamOpsError) {
          console.warn('Error fetching team operations for chart:', teamOpsError);
          // #region agent log
          // fetch('http://127.0.0.1:7247/ingest/69caa0a3-7f19-4041-86d8-9ea723023a00',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'EmployeePerformanceComparisonPage.js:fetchChartData','message':'Team operations API error','data':{error:teamOpsError?.message || String(teamOpsError)},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'C'})}).catch(()=>{});
          // #endregion
        }
        
        // Helper function to check if a date is a working day (Mon-Fri)
        const isWorkingDay = (date) => {
          const dayOfWeek = date.getDay();
          return dayOfWeek >= 1 && dayOfWeek <= 5; // Monday (1) to Friday (5)
        };
        
        // Helper function to format date as YYYY-MM-DD using local timezone
        const formatDateKey = (date) => {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        };
        
        // Calculate labels based on period (same for all metrics)
        let labels = [];
        if (selectedPeriod === 'Today') {
          labels = Array.from({ length: 24 }, (_, i) => `${i}:00`);
        } else if (selectedPeriod === 'This Week' && daysDiff <= 7) {
          // For "This Week", show day names
          labels = [
            t('performance.daysShort.mon'),
            t('performance.daysShort.tue'),
            t('performance.daysShort.wed'),
            t('performance.daysShort.thu'),
            t('performance.daysShort.fri'),
            t('performance.daysShort.sat'),
            t('performance.daysShort.sun'),
          ];
        } else if (daysDiff <= 31 || (selectedPeriod === 'Custom Range' && daysDiff <= 31)) {
          // For <= 31 days or custom ranges with <= 31 days, show actual dates
          const dayLabels = {};
          const currentDate = new Date(startDate);
          const sortedDateKeys = [];
          
          while (currentDate <= endDate) {
            const dateKey = formatDateKey(currentDate);
            const day = currentDate.getDate();
            const month = currentDate.toLocaleString(getDateLocale(), { month: 'short' });
            dayLabels[dateKey] = `${month} ${day}`;
            sortedDateKeys.push(dateKey);
            currentDate.setDate(currentDate.getDate() + 1);
          }
          
          sortedDateKeys.sort();
          const firstDate = new Date(sortedDateKeys[0]);
          const lastDate = new Date(sortedDateKeys[sortedDateKeys.length - 1]);
          const allSameMonth = firstDate.getMonth() === lastDate.getMonth() && 
                               firstDate.getFullYear() === lastDate.getFullYear();
          
          if (allSameMonth) {
            labels = sortedDateKeys.map(dateKey => {
              const date = new Date(dateKey);
              return date.getDate().toString();
            });
          } else {
            labels = sortedDateKeys.map(dateKey => dayLabels[dateKey]);
          }
        } else if (daysDiff <= 90) {
          const currentDate = new Date(startDate);
          let weekNum = 1;
          const weekKeys = [];
          
          while (currentDate <= endDate) {
            weekKeys.push(`Week ${weekNum}`);
            currentDate.setDate(currentDate.getDate() + 7);
            weekNum++;
          }
          
          labels = weekKeys;
        } else {
          const currentDate = new Date(startDate);
          const monthLabels = [];
          
          while (currentDate <= endDate) {
            const monthLabel = currentDate.toLocaleString('default', { month: 'short', year: 'numeric' });
            monthLabels.push(monthLabel);
            currentDate.setMonth(currentDate.getMonth() + 1);
          }
          
          labels = monthLabels;
        }
        
        // Calculate data for all three metrics
        // Helper function to process data for a specific metric
        const processMetricData = (userItems, teamItems, metricType) => {
          let userMetricData = [];
          let teamMetricData = [];
          
          if (selectedPeriod === 'Today') {
          const hourCounts = Array(24).fill(0);
          const teamHourCounts = Array(24).fill(0);
          
          // For operations metric, use Sets to track distinct operations
          const hourOperationSets = Array(24).fill(null).map(() => new Set());
          const teamHourOperationSets = Array(24).fill(null).map(() => new Set());
          
            userItems.forEach(item => {
              const date = new Date(item.date_saisie);
            const hour = date.getHours();
              if (metricType === 'operations' && item.etat_final === completedEtat && item.operation?.id) {
                // Count distinct operations
                hourOperationSets[hour].add(item.operation.id);
              } else if (metricType === 'imputations') {
            hourCounts[hour]++;
              } else if (metricType === 'hours') {
                const workedTime = item.temps_standard ?? item.operation?.temps_standard;
                hourCounts[hour] += convertTempsStandardToHours(workedTime);
              }
          });
          
            teamItems.forEach(item => {
              const date = new Date(item.date_saisie);
            const hour = date.getHours();
              if (metricType === 'operations' && item.etat_final === completedEtat && item.operation?.id) {
                // Count distinct operations
                teamHourOperationSets[hour].add(item.operation.id);
              } else if (metricType === 'imputations') {
            teamHourCounts[hour]++;
              } else if (metricType === 'hours') {
                const workedTime = item.temps_standard ?? item.operation?.temps_standard;
                teamHourCounts[hour] += convertTempsStandardToHours(workedTime);
              }
            });
            
            // For operations, use Set sizes; for others, use counts
            if (metricType === 'operations') {
              userMetricData = hourOperationSets.map(set => set.size);
              teamMetricData = teamMemberCount > 0 
                ? teamHourOperationSets.map(set => Math.round((set.size / teamMemberCount) * 10) / 10)
                : Array(24).fill(0);
            } else {
              userMetricData = hourCounts;
              teamMetricData = teamMemberCount > 0 
            ? teamHourCounts.map(count => Math.round((count / teamMemberCount) * 10) / 10)
            : Array(24).fill(0);
            }
        } else if (selectedPeriod === 'This Week' && daysDiff <= 7) {
          const dayCounts = { Mon: 0, Tue: 0, Wed: 0, Thu: 0, Fri: 0, Sat: 0, Sun: 0 };
          const teamDayCounts = { Mon: 0, Tue: 0, Wed: 0, Thu: 0, Fri: 0, Sat: 0, Sun: 0 };
          
          // For operations metric, use Sets to track distinct operations
          const dayOperationSets = { Mon: new Set(), Tue: new Set(), Wed: new Set(), Thu: new Set(), Fri: new Set(), Sat: new Set(), Sun: new Set() };
          const teamDayOperationSets = { Mon: new Set(), Tue: new Set(), Wed: new Set(), Thu: new Set(), Fri: new Set(), Sat: new Set(), Sun: new Set() };
          
            userItems.forEach(item => {
              const dateStr = item.date_operation;
            const [year, month, day] = dateStr.split('-').map(Number);
              const date = new Date(year, month - 1, day);
            const dayOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][date.getDay()];
            if (dayCounts.hasOwnProperty(dayOfWeek)) {
                if (metricType === 'operations' && item.etat_final === completedEtat && item.operation?.id) {
                  // Count distinct operations
                  dayOperationSets[dayOfWeek].add(item.operation.id);
                } else if (metricType === 'imputations') {
              dayCounts[dayOfWeek]++;
                } else if (metricType === 'hours') {
                  const workedTime = item.temps_standard ?? item.operation?.temps_standard;
                  dayCounts[dayOfWeek] += convertTempsStandardToHours(workedTime);
                }
              }
            });
            
            teamItems.forEach(item => {
              const dateStr = item.date_operation;
            const [year, month, day] = dateStr.split('-').map(Number);
              const date = new Date(year, month - 1, day);
            const dayOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][date.getDay()];
            if (teamDayCounts.hasOwnProperty(dayOfWeek)) {
                if (metricType === 'operations' && item.etat_final === completedEtat && item.operation?.id) {
                  // Count distinct operations
                  teamDayOperationSets[dayOfWeek].add(item.operation.id);
                } else if (metricType === 'imputations') {
              teamDayCounts[dayOfWeek]++;
                } else if (metricType === 'hours') {
                  const workedTime = item.temps_standard ?? item.operation?.temps_standard;
                  teamDayCounts[dayOfWeek] += convertTempsStandardToHours(workedTime);
                }
              }
            });
            
            // For operations, use Set sizes; for others, use counts
            if (metricType === 'operations') {
              userMetricData = [
                dayOperationSets.Mon.size,
                dayOperationSets.Tue.size,
                dayOperationSets.Wed.size,
                dayOperationSets.Thu.size,
                dayOperationSets.Fri.size,
                dayOperationSets.Sat.size,
                dayOperationSets.Sun.size
              ];
              teamMetricData = teamMemberCount > 0
                ? [
                    Math.round((teamDayOperationSets.Mon.size / teamMemberCount) * 10) / 10,
                    Math.round((teamDayOperationSets.Tue.size / teamMemberCount) * 10) / 10,
                    Math.round((teamDayOperationSets.Wed.size / teamMemberCount) * 10) / 10,
                    Math.round((teamDayOperationSets.Thu.size / teamMemberCount) * 10) / 10,
                    Math.round((teamDayOperationSets.Fri.size / teamMemberCount) * 10) / 10,
                    Math.round((teamDayOperationSets.Sat.size / teamMemberCount) * 10) / 10,
                    Math.round((teamDayOperationSets.Sun.size / teamMemberCount) * 10) / 10
                  ]
                : [0, 0, 0, 0, 0, 0, 0];
            } else {
              userMetricData = [dayCounts.Mon, dayCounts.Tue, dayCounts.Wed, dayCounts.Thu, dayCounts.Fri, dayCounts.Sat, dayCounts.Sun];
              teamMetricData = teamMemberCount > 0
            ? [
                Math.round((teamDayCounts.Mon / teamMemberCount) * 10) / 10,
                Math.round((teamDayCounts.Tue / teamMemberCount) * 10) / 10,
                Math.round((teamDayCounts.Wed / teamMemberCount) * 10) / 10,
                Math.round((teamDayCounts.Thu / teamMemberCount) * 10) / 10,
                Math.round((teamDayCounts.Fri / teamMemberCount) * 10) / 10,
                Math.round((teamDayCounts.Sat / teamMemberCount) * 10) / 10,
                Math.round((teamDayCounts.Sun / teamMemberCount) * 10) / 10
              ]
            : [0, 0, 0, 0, 0, 0, 0];
            }
        } else if (daysDiff <= 31) {
          const dayCounts = {};
          const teamDayCounts = {};
          const currentDate = new Date(startDate);
          
          // For operations metric, use Sets to track distinct operations
          const dayOperationSets = {};
          const teamDayOperationSets = {};
          
          while (currentDate <= endDate) {
            const dateKey = formatDateKey(currentDate);
            dayCounts[dateKey] = 0;
            teamDayCounts[dateKey] = 0;
            if (metricType === 'operations') {
              dayOperationSets[dateKey] = new Set();
              teamDayOperationSets[dateKey] = new Set();
            }
            currentDate.setDate(currentDate.getDate() + 1);
          }
          
            userItems.forEach(item => {
              const dateStr = item.date_operation;
            const [year, month, day] = dateStr.split('-').map(Number);
              const date = new Date(year, month - 1, day);
            const dateKey = formatDateKey(date);
            if (dayCounts.hasOwnProperty(dateKey)) {
                if (metricType === 'operations' && item.etat_final === completedEtat && item.operation?.id) {
                  // Count distinct operations
                  dayOperationSets[dateKey].add(item.operation.id);
                } else if (metricType === 'imputations') {
              dayCounts[dateKey]++;
                } else if (metricType === 'hours') {
                  const workedTime = item.temps_standard ?? item.operation?.temps_standard;
                  dayCounts[dateKey] += convertTempsStandardToHours(workedTime);
                }
              }
            });
            
            teamItems.forEach(item => {
              const dateStr = item.date_operation;
            const [year, month, day] = dateStr.split('-').map(Number);
              const date = new Date(year, month - 1, day);
            const dateKey = formatDateKey(date);
            if (teamDayCounts.hasOwnProperty(dateKey)) {
                if (metricType === 'operations' && item.etat_final === completedEtat && item.operation?.id) {
                  // Count distinct operations
                  teamDayOperationSets[dateKey].add(item.operation.id);
                } else if (metricType === 'imputations') {
              teamDayCounts[dateKey]++;
                } else if (metricType === 'hours') {
                  const workedTime = item.temps_standard ?? item.operation?.temps_standard;
                  teamDayCounts[dateKey] += convertTempsStandardToHours(workedTime);
                }
            }
          });
          
          const sortedDateKeys = Object.keys(dayCounts).sort();
            // For operations, use Set sizes; for others, use counts
            if (metricType === 'operations') {
              userMetricData = sortedDateKeys.map(dateKey => dayOperationSets[dateKey].size);
              teamMetricData = teamMemberCount > 0
                ? sortedDateKeys.map(dateKey => Math.round((teamDayOperationSets[dateKey]?.size || 0) / teamMemberCount * 10) / 10)
                : Array(sortedDateKeys.length).fill(0);
          } else {
              userMetricData = sortedDateKeys.map(dateKey => dayCounts[dateKey]);
              teamMetricData = teamMemberCount > 0
            ? sortedDateKeys.map(dateKey => Math.round((teamDayCounts[dateKey] || 0) / teamMemberCount * 10) / 10)
            : Array(sortedDateKeys.length).fill(0);
            }
        } else if (daysDiff <= 90) {
          const weekCounts = {};
          const teamWeekCounts = {};
          const currentDate = new Date(startDate);
          let weekNum = 1;
          
          // For operations metric, use Sets to track distinct operations
          const weekOperationSets = {};
          const teamWeekOperationSets = {};
          
          while (currentDate <= endDate) {
            const weekKey = `Week ${weekNum}`;
            weekCounts[weekKey] = 0;
            teamWeekCounts[weekKey] = 0;
            if (metricType === 'operations') {
              weekOperationSets[weekKey] = new Set();
              teamWeekOperationSets[weekKey] = new Set();
            }
            currentDate.setDate(currentDate.getDate() + 7);
            weekNum++;
          }
          
            userItems.forEach(item => {
              const dateStr = item.date_operation;
            const [year, month, day] = dateStr.split('-').map(Number);
              const date = new Date(year, month - 1, day);
            const weekStart = new Date(date);
            weekStart.setDate(date.getDate() - date.getDay());
            const weekIndex = Math.floor((weekStart - startDate) / (1000 * 60 * 60 * 24 * 7)) + 1;
            const weekKey = `Week ${weekIndex}`;
            if (weekCounts.hasOwnProperty(weekKey)) {
                if (metricType === 'operations' && item.etat_final === completedEtat && item.operation?.id) {
                  // Count distinct operations
                  weekOperationSets[weekKey].add(item.operation.id);
                } else if (metricType === 'imputations') {
              weekCounts[weekKey]++;
                } else if (metricType === 'hours') {
                  const workedTime = item.temps_standard ?? item.operation?.temps_standard;
                  weekCounts[weekKey] += convertTempsStandardToHours(workedTime);
                }
              }
            });
            
            teamItems.forEach(item => {
              const dateStr = item.date_operation;
            const [year, month, day] = dateStr.split('-').map(Number);
              const date = new Date(year, month - 1, day);
            const weekStart = new Date(date);
            weekStart.setDate(date.getDate() - date.getDay());
            const weekIndex = Math.floor((weekStart - startDate) / (1000 * 60 * 60 * 24 * 7)) + 1;
            const weekKey = `Week ${weekIndex}`;
            if (teamWeekCounts.hasOwnProperty(weekKey)) {
                if (metricType === 'operations' && item.etat_final === completedEtat && item.operation?.id) {
                  // Count distinct operations
                  teamWeekOperationSets[weekKey].add(item.operation.id);
                } else if (metricType === 'imputations') {
              teamWeekCounts[weekKey]++;
                } else if (metricType === 'hours') {
                  const workedTime = item.temps_standard ?? item.operation?.temps_standard;
                  teamWeekCounts[weekKey] += convertTempsStandardToHours(workedTime);
                }
              }
            });
            
            const weekKeys = Object.keys(weekCounts);
            // For operations, use Set sizes; for others, use counts
            if (metricType === 'operations') {
              userMetricData = weekKeys.map(weekKey => weekOperationSets[weekKey].size);
              teamMetricData = teamMemberCount > 0
                ? weekKeys.map(weekKey => Math.round((teamWeekOperationSets[weekKey]?.size || 0) / teamMemberCount * 10) / 10)
                : Array(weekKeys.length).fill(0);
        } else {
              userMetricData = Object.values(weekCounts);
              teamMetricData = teamMemberCount > 0
                ? weekKeys.map(weekKey => Math.round((teamWeekCounts[weekKey] || 0) / teamMemberCount * 10) / 10)
                : Array(weekKeys.length).fill(0);
            }
        } else {
          const monthCounts = {};
          const teamMonthCounts = {};
          const currentDate = new Date(startDate);
          
          // For operations metric, use Sets to track distinct operations
          const monthOperationSets = {};
          const teamMonthOperationSets = {};
          
          while (currentDate <= endDate) {
            const monthKey = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
              monthCounts[monthKey] = 0;
            teamMonthCounts[monthKey] = 0;
            if (metricType === 'operations') {
              monthOperationSets[monthKey] = new Set();
              teamMonthOperationSets[monthKey] = new Set();
            }
            currentDate.setMonth(currentDate.getMonth() + 1);
          }
          
            userItems.forEach(item => {
              const dateStr = item.date_operation;
            const [year, month, day] = dateStr.split('-').map(Number);
              const date = new Date(year, month - 1, day);
            const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
            if (monthCounts.hasOwnProperty(monthKey)) {
                if (metricType === 'operations' && item.etat_final === completedEtat && item.operation?.id) {
                  // Count distinct operations
                  monthOperationSets[monthKey].add(item.operation.id);
                } else if (metricType === 'imputations') {
                  monthCounts[monthKey]++;
                } else if (metricType === 'hours') {
                  const workedTime = item.temps_standard ?? item.operation?.temps_standard;
                  monthCounts[monthKey] += convertTempsStandardToHours(workedTime);
                }
              }
            });
            
            teamItems.forEach(item => {
              const dateStr = item.date_operation;
            const [year, month, day] = dateStr.split('-').map(Number);
              const date = new Date(year, month - 1, day);
            const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
            if (teamMonthCounts.hasOwnProperty(monthKey)) {
                if (metricType === 'operations' && item.etat_final === completedEtat && item.operation?.id) {
                  // Count distinct operations
                  teamMonthOperationSets[monthKey].add(item.operation.id);
                } else if (metricType === 'imputations') {
              teamMonthCounts[monthKey]++;
                } else if (metricType === 'hours') {
                  const workedTime = item.temps_standard ?? item.operation?.temps_standard;
                  teamMonthCounts[monthKey] += convertTempsStandardToHours(workedTime);
                }
              }
            });
            
            const monthKeys = Object.keys(monthCounts);
            // For operations, use Set sizes; for others, use counts
            if (metricType === 'operations') {
              userMetricData = monthKeys.map(monthKey => monthOperationSets[monthKey].size);
              teamMetricData = teamMemberCount > 0
                ? monthKeys.map(monthKey => Math.round((teamMonthOperationSets[monthKey]?.size || 0) / teamMemberCount * 10) / 10)
                : Array(monthKeys.length).fill(0);
            } else {
              userMetricData = Object.values(monthCounts);
              teamMetricData = teamMemberCount > 0
                ? monthKeys.map(monthKey => Math.round((teamMonthCounts[monthKey] || 0) / teamMemberCount * 10) / 10)
                : Array(monthKeys.length).fill(0);
            }
          }
          
          return { userData: userMetricData, teamData: teamMetricData };
        };
        
        // Calculate data for all three metrics
        const operationsData = processMetricData(imputations, teamOperations.filter(op => op.etat_final === completedEtat), 'operations');
        const imputationsData = processMetricData(allImputations, teamOperations, 'imputations');
        // Worked hours: Accomplished (100%) + Failed (50%); use imputation.temps_standard
        const workedHoursImputations = allImputations.filter(imp => isWorkedHoursStatus(imp.etat_final));
        const workedHoursTeamOps = teamOperations.filter(op => isWorkedHoursStatus(op.etat_final));
        const hoursData = processMetricData(workedHoursImputations, workedHoursTeamOps, 'hours');
        
        // Ensure data arrays match labels length (pad with zeros if needed, truncate if too long)
        const ensureDataLength = (dataArray, expectedLength) => {
          if (dataArray.length === expectedLength) return dataArray;
          if (dataArray.length < expectedLength) {
            return [...dataArray, ...Array(expectedLength - dataArray.length).fill(0)];
          }
          return dataArray.slice(0, expectedLength);
        };
        
        const expectedLength = labels.length;
        const normalizedOperationsData = {
          userData: ensureDataLength(operationsData.userData, expectedLength),
          teamData: ensureDataLength(operationsData.teamData, expectedLength)
        };
        const normalizedImputationsData = {
          userData: ensureDataLength(imputationsData.userData, expectedLength),
          teamData: ensureDataLength(imputationsData.teamData, expectedLength)
        };
        const normalizedHoursData = {
          userData: ensureDataLength(hoursData.userData, expectedLength),
          teamData: ensureDataLength(hoursData.teamData, expectedLength)
        };
        
        // #region agent log
        // fetch('http://127.0.0.1:7247/ingest/69caa0a3-7f19-4041-86d8-9ea723023a00',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'EmployeePerformanceComparisonPage.js:fetchChartData','message':'Setting chart data','data':{selectedPeriod,labelsCount:labels.length,userDataLength:normalizedOperationsData.userData.length,teamDataLength:normalizedOperationsData.teamData.length,labelsSample:labels.slice(0,5),userDataSample:normalizedOperationsData.userData.slice(0,5),teamAvgDataSample:normalizedOperationsData.teamData.slice(0,5),teamMemberCount},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'D'})}).catch(()=>{});
        // #endregion
        setChartData({
          labels,
          userData: normalizedOperationsData.userData, // Default to operations for backward compatibility
          teamAvgData: normalizedOperationsData.teamData,
          loading: false,
          operations: {
            userData: normalizedOperationsData.userData,
            teamAvgData: normalizedOperationsData.teamData
          },
          imputations: {
            userData: normalizedImputationsData.userData,
            teamAvgData: normalizedImputationsData.teamData
          },
          hours: {
            userData: normalizedHoursData.userData,
            teamAvgData: normalizedHoursData.teamData
          }
        });
      } catch (error) {
        console.error('Error fetching chart data:', error);
        setChartData(prev => ({ ...prev, loading: false }));
      }
    };

    fetchChartData();
  }, [selectedTeam, selectedPeriod, customDateRange, selectedGraphMetric]);

  // Switch away from 'hours' metric when period is 'Today'
  useEffect(() => {
    if (selectedPeriod === 'Today' && selectedGraphMetric === 'hours') {
      setSelectedGraphMetric('operations');
    }
  }, [selectedPeriod, selectedGraphMetric]);

  // Reset activity log page to 1 when filter changes
  useEffect(() => {
    setActivityLogPage(1);
  }, [operationNameFilter, selectedTeam, selectedPeriod, customDateRange]);

  // Fetch activity log data
  useEffect(() => {
    const fetchActivityLog = async () => {
      try {
        setActivityLog(prev => ({ ...prev, loading: true }));
        
        // Get filtered date range
        const dateRange = getFilteredDateRange(selectedPeriod, customDateRange);
        
        // Fetch successful operations (TERMINE) for the filtered period
        // Fetch a large number to allow client-side filtering and pagination
        const imputationsResponse = await getImputations({
          date_from: dateRange.date_from,
          date_to: dateRange.date_to,
          etat_final: completedEtat
        }, 1, 1000);

        let imputations = imputationsResponse?.results || [];
        const totalFromAPI = imputationsResponse?.count || imputations.length;
        
        // Apply operation name filter (client-side)
        if (operationNameFilter.trim()) {
          const term = operationNameFilter.toLowerCase();
          imputations = imputations.filter(imp => {
            return imp.operation?.name?.toLowerCase().includes(term) || 
                   imp.operation?.production_line_lp?.toLowerCase().includes(term);
          });
        }
        
        // Sort by date_operation descending (most recent first)
        const sortedImputations = [...imputations].sort((a, b) => {
          const dateA = new Date(a.date_operation);
          const dateB = new Date(b.date_operation);
          return dateB - dateA;
        });
        
        // Apply pagination (client-side)
        const startIndex = (activityLogPage - 1) * activityLogPageSize;
        const endIndex = startIndex + activityLogPageSize;
        const paginatedImputations = sortedImputations.slice(startIndex, endIndex);
        
        setActivityLog({
          data: paginatedImputations,
          loading: false,
          total: imputations.length // Total after filtering
        });
      } catch (error) {
        console.error('Error fetching activity log:', error);
        setActivityLog(prev => ({ ...prev, loading: false }));
      }
    };

    fetchActivityLog();
  }, [selectedTeam, selectedPeriod, customDateRange, activityLogPage, activityLogPageSize, operationNameFilter]);

  // Format timestamp for display
  const formatTimestamp = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const month = date.toLocaleString('default', { month: 'short' });
    const day = date.getDate();
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    const displayMinutes = String(minutes).padStart(2, '0');
    return `${month} ${day}, ${displayHours}:${displayMinutes} ${ampm}`;
  };

  // Format duration (temps_standard) for display
  const formatDuration = (tempsStandard) => {
    if (!tempsStandard) return '-';
    // Assuming temps_standard is in minutes
    const minutes = parseInt(tempsStandard);
    if (minutes < 60) {
      return `${minutes}m`;
    }
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  // Calculate dynamic y-axis max based on chart data for selected metric
  const calculateYAxisMax = () => {
    const currentData = chartData[selectedGraphMetric] || { userData: [], teamAvgData: [] };
    
    if (!currentData.userData.length && !currentData.teamAvgData.length) {
      return 60; // Default max if no data
    }
    
    // Find the maximum value across both datasets
    const maxUserValue = Math.max(...currentData.userData, 0);
    const maxTeamValue = Math.max(...currentData.teamAvgData, 0);
    const maxValue = Math.max(maxUserValue, maxTeamValue);
    
    // Add 20% padding above the max value, with a minimum of 10
    // Round up to nearest 5 for cleaner scale
    const paddedMax = Math.ceil(maxValue * 1.2 / 5) * 5;
    return Math.max(paddedMax, 10);
  };

  // Calculate dynamic step size based on y-axis max
  const calculateStepSize = (maxValue) => {
    // Divide max into 3-4 nice intervals
    if (maxValue <= 20) return 5;
    if (maxValue <= 50) return 10;
    if (maxValue <= 100) return 20;
    if (maxValue <= 200) return 50;
    return Math.ceil(maxValue / 4 / 10) * 10; // Round to nearest 10
  };

  const yAxisMax = calculateYAxisMax();
  const stepSize = calculateStepSize(yAxisMax);

  // Prepare chart data structure for Chart.js based on selected metric
  const currentMetricData = chartData[selectedGraphMetric] || { userData: [], teamAvgData: [] };
  const userLineColor = isRsPerf ? RS_CHART_PERF_USER : '#135bec';
  const teamLineColor = isRsPerf ? '#a3a3a3' : '#d1d5db';
  const chartDataForDisplay = {
    labels: chartData.labels,
    datasets: [
      {
        label: t('performance.teamAvg'),
        data: currentMetricData.teamAvgData || [],
        borderColor: teamLineColor,
        backgroundColor: 'transparent',
        borderWidth: 2,
        tension: 0.4, // Smooth curves
        pointRadius: 0, // Hide default points for team avg
        pointHoverRadius: 4,
        fill: false,
      },
      {
        label: t('performance.you'),
        data: currentMetricData.userData || [],
        borderColor: userLineColor,
        backgroundColor: 'transparent',
        borderWidth: 3,
        tension: 0.4, // Smooth curves
        pointRadius: 5,
        pointHoverRadius: 6,
        pointBackgroundColor: '#ffffff',
        pointBorderColor: userLineColor,
        pointBorderWidth: 2,
        fill: false,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false, // We have custom legend
      },
      tooltip: {
        enabled: true,
        mode: 'index',
        intersect: false,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleFont: {
          size: 12,
          weight: 'bold',
        },
        bodyFont: {
          size: 11,
        },
        callbacks: {
          label: function(context) {
            const value = context.parsed.y;
            // Format as HH:MM for hours metric, otherwise show as number
            const displayValue = selectedGraphMetric === 'hours' 
              ? formatHoursToHHMM(value)
              : value;
            return context.dataset.label + ': ' + displayValue;
          }
        }
      },
    },
    scales: {
      x: {
        display: true,
        grid: {
          display: false,
        },
        ticks: {
          color: '#616f89',
          font: {
            size: 11,
            weight: 500,
          },
          maxRotation: 45,
          minRotation: 0,
        },
        border: {
          display: false,
        },
      },
      y: {
        display: true, // Need to display to show grid lines
        position: 'right',
        min: 0,
        max: yAxisMax,
        grid: {
          color: '#f3f4f6', // gray-100
          lineWidth: 1,
          drawBorder: false, // Hide the axis border line
        },
        ticks: {
          stepSize: stepSize,
          display: false, // Hide Y-axis ticks/labels
        },
        border: {
          display: false, // Hide the axis border
        },
      },
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false,
    },
  };

  return (
    <div className="min-h-screen w-full bg-[#f6f6f8] dark:bg-slate-900">
      <div className="mx-auto px-4 sm:px-6 lg:px-8">
        {/* Page Header */}
        <div className="mb-8">
          {/* <h1 className="text-3xl font-bold text-[#111318] tracking-tight mb-2">
            Performance 
          </h1>
          <p className="text-[#616f89] text-base">
            Track your operations performance and compare it with your team averages. Monitor your daily, weekly, and monthly progress.
          </p> */}
        </div>

        {/* Stats Section */}
        <section className="grid grid-cols-1 gap-4 sm:grid-cols-3 lg:gap-6 mb-8">
          {/* Operations Today */}
          <div className="relative overflow-hidden rounded-xl border border-[#e5e7eb] dark:border-slate-600 bg-white dark:bg-slate-800 p-6 ">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-sm font-medium text-[#616f89] dark:text-slate-400">{t('performance.operationsToday')}</h3>
              {/* <span className="flex items-center text-xs font-medium text-[#07883b] bg-[#07883b]/10 px-2 py-0.5 rounded-full">
                <span className="material-symbols-outlined mr-1" style={{ fontSize: '14px' }}>trending_up</span>
                +12%
              </span> */}
            </div>
            <div className="flex items-baseline gap-2">
              {operationsStats.loading ? (
                <span className="text-4xl font-bold tracking-tight text-[#111318] dark:text-slate-100">...</span>
              ) : (
                <span className="text-4xl font-bold tracking-tight text-[#111318] dark:text-slate-100">{operationsStats.today}</span>
              )}
            </div>
            {/* <div className="absolute bottom-0 left-0 h-1 w-full bg-gradient-to-r from-[#135bec] to-transparent opacity-0 transition-opacity group-hover:opacity-100"></div> */}
          </div>

          {/* Operations Week */}
          <div className="relative overflow-hidden rounded-xl border border-[#e5e7eb] dark:border-slate-600 bg-white dark:bg-slate-800 p-6 ">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-sm font-medium text-[#616f89] dark:text-slate-400">{t('performance.operationsWeek')}</h3>
              {/* <span className="flex items-center text-xs font-medium text-[#07883b] bg-[#07883b]/10 px-2 py-0.5 rounded-full">
                <span className="material-symbols-outlined mr-1" style={{ fontSize: '14px' }}>trending_up</span>
                +5%
              </span> */}
            </div>
            <div className="flex items-baseline gap-2">
              {operationsStats.loading ? (
                <span className="text-4xl font-bold tracking-tight text-[#111318] dark:text-slate-100">...</span>
              ) : (
                <span className="text-4xl font-bold tracking-tight text-[#111318] dark:text-slate-100">{operationsStats.week}</span>
              )}
            </div>
            {/* <div className="absolute bottom-0 left-0 h-1 w-full bg-gradient-to-r from-[#135bec] to-transparent opacity-0 transition-opacity group-hover:opacity-100"></div> */}
          </div>

          {/* Operations Month */}
          <div className="relative overflow-hidden rounded-xl border border-[#e5e7eb] dark:border-slate-600 bg-white dark:bg-slate-800 p-6 ">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-sm font-medium text-[#616f89] dark:text-slate-400">{t('performance.operationsMonth')}</h3>
              {/* <span className="flex items-center text-xs font-medium text-[#07883b] bg-[#07883b]/10 px-2 py-0.5 rounded-full">
                <span className="material-symbols-outlined mr-1" style={{ fontSize: '14px' }}>trending_up</span>
                +8%
              </span> */}
            </div>
            <div className="flex items-baseline gap-2">
              {operationsStats.loading ? (
                <span className="text-4xl font-bold tracking-tight text-[#111318] dark:text-slate-100">...</span>
              ) : (
                <span className="text-4xl font-bold tracking-tight text-[#111318] dark:text-slate-100">{operationsStats.month}</span>
              )}
            </div>
            {/* <div className="absolute bottom-0 left-0 h-1 w-full bg-gradient-to-r from-[#135bec] to-transparent opacity-0 transition-opacity group-hover:opacity-100"></div> */}
          </div>
        </section>

        {/* Worked Hours Progress Bar */}
        <section className="mb-8">
          <div className="rounded-xl border border-[#e5e7eb] dark:border-slate-600 bg-white dark:bg-slate-800 p-6 shadow-sm">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h3 className="text-sm font-medium text-[#616f89] dark:text-slate-400 mb-1">{t('performance.workedHoursToday')}</h3>
                <div className="flex items-baseline gap-2">
                  {workedHours.loading ? (
                    <span className="text-2xl font-bold tracking-tight text-[#111318] dark:text-slate-100">...</span>
                  ) : (
                    <>
                      <span className="text-2xl font-bold tracking-tight text-[#111318] dark:text-slate-100">
                        {formatHoursAndMinutes(workedHours.hours || 0)}
                      </span>
                      <span className="text-sm text-[#616f89] dark:text-slate-400">/ {formatHoursAndMinutes(dailyObjectiveHours)}</span>
                    </>
                  )}
                </div>
              </div>
              {!workedHours.loading && (
                <div className="text-right">
                  <span className="text-sm font-medium text-[#111318] dark:text-slate-100">
                    {Math.min(Math.round(((workedHours.hours || 0) / dailyObjectiveHours) * 100), 100)}%
                  </span>
                  <p className="text-xs text-[#616f89] dark:text-slate-400">{t('performance.ofDailyObjective')}</p>
                </div>
              )}
            </div>
            <div className="w-full h-3 bg-gray-100 dark:bg-slate-700 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-[#135bec] to-[#0d47a1] rounded-full transition-all duration-500 ease-out"
                style={{ 
                  width: `${Math.min(((workedHours.hours || 0) / dailyObjectiveHours) * 100, 100)}%`,
                  maxWidth: '100%'
                }}
              ></div>
            </div>
          </div>
        </section>

        {/* Filters Section */}
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-[#e5e7eb] dark:border-slate-600 p-4 mb-6 shadow-sm">
          <div className="flex flex-wrap items-center gap-4">
            {/* Period Filter */}
            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-[#616f89] dark:text-slate-400">{t('performance.period')}</span>
              <select
                className="form-select block w-full rounded-lg border border-[#dbe0e6] dark:border-slate-600 bg-[#f9fafb] dark:bg-slate-700 text-sm text-[#111318] dark:text-slate-100 focus:border-[#135bec] focus:ring-[#135bec] h-10 px-3 min-w-[160px]"
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value)}
              >
                <option value="Today">{t('performance.periods.today')}</option>
                <option value="This Week">{t('performance.periods.thisWeek')}</option>
                <option value="This Month">{t('performance.periods.thisMonth')}</option>
                <option value="Last Month">{t('performance.periods.lastMonth')}</option>
                <option value="Last 3 Months">{t('performance.periods.last3Months')}</option>
                <option value="Custom Range">{t('performance.periods.customRange')}</option>
              </select>
            </label>

            {/* Date Range Picker (shown when Custom Range is selected) */}
            {selectedPeriod === 'Custom Range' && (
              <>
                <label className="flex flex-col gap-1.5">
                  <span className="text-xs font-medium text-[#616f89] dark:text-slate-400">{t('performance.startDate')}</span>
                  <input
                    type="date"
                    className="block w-full rounded-lg border border-[#dbe0e6] dark:border-slate-600 bg-[#f9fafb] dark:bg-slate-700 text-sm text-[#111318] dark:text-slate-100 focus:border-[#135bec] focus:ring-[#135bec] h-10 px-3 min-w-[150px]"
                    value={customDateRange.startDate}
                    onChange={(e) => setCustomDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                  />
                </label>
                <label className="flex flex-col gap-1.5">
                  <span className="text-xs font-medium text-[#616f89] dark:text-slate-400">{t('performance.endDate')}</span>
                  <input
                    type="date"
                    className="block w-full rounded-lg border border-[#dbe0e6] dark:border-slate-600 bg-[#f9fafb] dark:bg-slate-700 text-sm text-[#111318] dark:text-slate-100 focus:border-[#135bec] focus:ring-[#135bec] h-10 px-3 min-w-[150px]"
                    value={customDateRange.endDate}
                    onChange={(e) => setCustomDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                  />
                </label>
              </>
            )}
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3 mb-8">
          {/* Left Column */}
          <div className="space-y-6 lg:col-span-1">
            {/* Team Positioning */}
            <div className="rounded-xl border border-[#e5e7eb] dark:border-slate-600 bg-white dark:bg-slate-800 p-6 shadow-sm">
              <h3 className="mb-4 text-base font-semibold text-[#111318] dark:text-slate-100">{t('performance.teamPositioning')}</h3>
              {(
                <>
                  {/* Tabs for different metrics */}
                  <div className="mb-4 relative flex border-b border-gray-200 dark:border-slate-600">
                    <button
                      onClick={() => setSelectedRankingMetric('operations')}
                      className={`flex-1 px-3 py-2 text-xs font-medium transition-all duration-300 ease-in-out relative z-10 ${
                        selectedRankingMetric === 'operations'
                          ? 'text-[#135bec] dark:text-primary'
                          : 'text-[#616f89] hover:text-[#111318] dark:text-slate-400 dark:hover:text-slate-200'
                      }`}
                    >
                      Operations
                    </button>
                    <button
                      onClick={() => setSelectedRankingMetric('imputations')}
                      className={`flex-1 px-3 py-2 text-xs font-medium transition-all duration-300 ease-in-out relative z-10 ${
                        selectedRankingMetric === 'imputations'
                          ? 'text-[#135bec] dark:text-primary'
                          : 'text-[#616f89] hover:text-[#111318] dark:text-slate-400 dark:hover:text-slate-200'
                      }`}
                    >
                      Imputations
                    </button>
                    <button
                      onClick={() => setSelectedRankingMetric('hours')}
                      className={`flex-1 px-3 py-2 text-xs font-medium transition-all duration-300 ease-in-out relative z-10 ${
                        selectedRankingMetric === 'hours'
                          ? 'text-[#135bec] dark:text-primary'
                          : 'text-[#616f89] hover:text-[#111318] dark:text-slate-400 dark:hover:text-slate-200'
                      }`}
                    >
                      Hours
                    </button>
                    {/* Animated indicator */}
                    <div 
                      className="absolute bottom-0 h-0.5 bg-[#135bec] transition-all duration-300 ease-in-out"
                      style={{
                        width: '33.333%',
                        left: selectedRankingMetric === 'operations' 
                          ? '0%' 
                          : selectedRankingMetric === 'imputations' 
                            ? '33.333%' 
                            : '66.666%'
                      }}
                    />
                  </div>
                  
                  <div className="flex flex-col gap-4">
                    <div className="flex items-center justify-between rounded-lg bg-[#135bec]/5 dark:bg-primary/20 p-4 transition-all duration-300 ease-in-out">
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#135bec]/20 dark:bg-primary/30 text-[#135bec] dark:text-primary">
                          <span className="material-symbols-outlined">leaderboard</span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-[#111318] dark:text-slate-100">
                            {teamPositioning.rankings?.[selectedRankingMetric]?.rank !== null && teamPositioning.rankings?.[selectedRankingMetric]?.rank !== undefined
                              ? `Rank #${teamPositioning.rankings[selectedRankingMetric].rank}${teamPositioning.totalTeamMembers > 0 ? ` of ${teamPositioning.totalTeamMembers}` : ''}`
                              : 'N/A'}
                          </p>
                          <p className="text-xs text-[#616f89] dark:text-slate-400">
                            {selectedRankingMetric === 'operations' && `Operations: ${teamPositioning.rankings?.operations?.value || 0}`}
                            {selectedRankingMetric === 'imputations' && `Imputations: ${teamPositioning.rankings?.imputations?.value || 0}`}
                            {selectedRankingMetric === 'hours' && `Hours: ${formatHoursAndMinutes(teamPositioning.rankings?.hours?.value || 0)}`}
                          </p>
                          <p className="text-xs text-[#616f89] dark:text-slate-400 mt-0.5">{t('performance.inTeam', { team: teamPositioning.teamName || t('performance.yourTeam') })}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Quality Score */}
            <div className="rounded-xl border border-[#e5e7eb] dark:border-slate-600 bg-white dark:bg-slate-800 p-6 shadow-sm">
              <h3 className="mb-4 text-base font-semibold text-[#111318] dark:text-slate-100">{t('performance.qualityScore')}</h3>
              {(
                <>
                  <div className="flex items-center justify-center py-2 relative">
                    <svg className="h-32 w-32 rotate-[-90deg]" viewBox="0 0 36 36">
                      <path className="text-gray-100 dark:text-slate-700" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="currentColor" strokeWidth="3"></path>
                      <path 
                        className="text-[#135bec]" 
                        d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" 
                        fill="none" 
                        stroke="currentColor" 
                        strokeDasharray={`${qualityScore.score}, 100`} 
                        strokeWidth="3"
                      ></path>
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <span className="text-2xl font-bold text-[#111318] dark:text-slate-100">{qualityScore.score}%</span>
                      <span className="text-xs text-[#616f89] dark:text-slate-400">
                        {qualityScore.score >= 90 ? t('performance.excellent') : qualityScore.score >= 75 ? t('performance.good') : qualityScore.score >= 60 ? t('performance.fair') : t('performance.needsImprovement')}
                      </span>
                    </div>
                  </div>
                  <div className="mt-4 space-y-2">
                    <div className="grid grid-cols-2 gap-2 text-xs text-[#616f89] dark:text-slate-400">
                      <div className="flex items-center gap-1.5">
                        <div className="h-2 w-2 rounded-full bg-green-500"></div>
                      <span>{t('performance.completedCount', { count: qualityScore.completed })}</span>
                    </div>
                      <div className="flex items-center gap-1.5">
                        <div className="h-2 w-2 rounded-full bg-red-500"></div>
                      <span>{t('performance.failedCount', { count: qualityScore.failed })}</span>
                      </div>
                      {/* <div className="flex items-center gap-1.5">
                        <div className="h-2 w-2 rounded-full bg-yellow-500"></div>
                        <span>Pending ({qualityScore.pending})</span>
                      </div> */}
                      {/* <div className="flex items-center gap-1.5">
                        <div className="h-2 w-2 rounded-full bg-gray-400"></div>
                        <span>Cancelled ({qualityScore.cancelled})</span>
                      </div> */}
                      <div className="flex items-center gap-1.5">
                        <div className="h-2 w-2 rounded-full bg-gray-300 dark:bg-slate-500"></div>
                        <span>{t('performance.totalCount', { count: qualityScore.total })}</span>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Right Column - Performance Graphs */}
          <div className="lg:col-span-2">
            <div className="flex h-full flex-col rounded-xl border border-[#e5e7eb] dark:border-slate-600 bg-white dark:bg-slate-800 p-6 shadow-sm">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-4">
                <h3 className="text-base font-semibold text-[#111318] dark:text-slate-100">{t('performance.performanceGraphs')}</h3>
                <div className="flex items-center gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="h-3 w-3 rounded-full bg-[#135bec] border-2 border-white dark:border-slate-800 shadow-sm"></span>
                    <span className="text-[#111318] dark:text-slate-100 font-medium">{t('performance.you')}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="h-3 w-3 rounded-full bg-gray-300 dark:bg-slate-600"></span>
                    <span className="text-[#616f89] dark:text-slate-400">{t('performance.teamAvg')}</span>
                  </div>
                </div>
              </div>
              
              {/* Tabs for different metrics */}
              <div className="mb-6 relative flex border-b border-gray-200 dark:border-slate-600">
                <button
                  onClick={() => setSelectedGraphMetric('operations')}
                  className={`flex-1 px-3 py-2 text-xs font-medium transition-all duration-300 ease-in-out relative z-10 ${
                    selectedGraphMetric === 'operations'
                      ? 'text-[#135bec] dark:text-primary'
                      : 'text-[#616f89] hover:text-[#111318] dark:text-slate-400 dark:hover:text-slate-200'
                  }`}
                >
                  Operations
                </button>
                <button
                  onClick={() => setSelectedGraphMetric('imputations')}
                  className={`flex-1 px-3 py-2 text-xs font-medium transition-all duration-300 ease-in-out relative z-10 ${
                    selectedGraphMetric === 'imputations'
                      ? 'text-[#135bec] dark:text-primary'
                      : 'text-[#616f89] hover:text-[#111318] dark:text-slate-400 dark:hover:text-slate-200'
                  }`}
                >
                  Imputations
                </button>
                {selectedPeriod !== 'Today' && (
                  <button
                    onClick={() => setSelectedGraphMetric('hours')}
                    className={`flex-1 px-3 py-2 text-xs font-medium transition-all duration-300 ease-in-out relative z-10 ${
                      selectedGraphMetric === 'hours'
                        ? 'text-[#135bec] dark:text-primary'
                        : 'text-[#616f89] hover:text-[#111318] dark:text-slate-400 dark:hover:text-slate-200'
                    }`}
                  >
                    Hours
                  </button>
                )}
                {/* Animated indicator */}
                <div 
                  className="absolute bottom-0 h-0.5 bg-[#135bec] transition-all duration-300 ease-in-out"
                  style={{
                    width: selectedPeriod === 'Today' ? '50%' : '33.333%',
                    left: selectedGraphMetric === 'operations' 
                      ? '0%' 
                      : selectedGraphMetric === 'imputations' 
                        ? (selectedPeriod === 'Today' ? '50%' : '33.333%')
                        : '66.666%'
                  }}
                />
              </div>
              
              <div className="relative flex-1 min-h-[300px] w-full bg-gradient-to-b from-transparent to-[#135bec]/5 dark:to-slate-800/50 rounded-lg border border-gray-200 dark:border-slate-600 p-4">
                {/* Custom Y-axis labels */}
                <div className="absolute left-4 top-4 bottom-12 flex flex-col justify-between text-xs text-[#616f89] dark:text-slate-400 z-10 pointer-events-none">
                  {(() => {
                    // Generate 4 evenly spaced Y-axis labels
                    const numLabels = 4;
                    const interval = yAxisMax / (numLabels - 1);
                    const labels = [];
                    for (let i = 0; i < numLabels; i++) {
                      const value = yAxisMax - (i * interval);
                      // Format as HH:MM for hours metric, otherwise show as number
                      const displayValue = selectedGraphMetric === 'hours' 
                        ? formatHoursToHHMM(value)
                        : Math.round(value);
                      labels.push(<span key={i}>{displayValue}</span>);
                    }
                    return labels;
                  })()}
                </div>
                {/* Chart container with proper spacing for Y-axis labels */}
                <div className="ml-12 mr-4 mt-4 mb-10 h-[calc(100%-3.5rem)]">
                  <Line key={`${selectedGraphMetric}-${selectedPeriod}-${customDateRange.startDate}-${customDateRange.endDate}`} data={chartDataForDisplay} options={chartOptions} />
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Activity Log Section */}
        <section className="rounded-xl border border-[#e5e7eb] dark:border-slate-600 bg-white dark:bg-slate-800 shadow-sm overflow-hidden">
          <div className="border-b border-[#e5e7eb] dark:border-slate-600 px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <h3 className="text-base font-semibold text-[#111318] dark:text-slate-100">{t('performance.activityLog')}</h3>
            <div className="flex items-center gap-2">
              <div className="relative">
                <span className="material-symbols-outlined absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500" style={{ fontSize: '18px' }}>search</span>
                <input 
                  className="h-9 w-64 rounded-lg border border-gray-200 dark:border-slate-600 bg-[#f6f6f8] dark:bg-slate-700 pl-9 text-sm focus:border-[#135bec] focus:ring-1 focus:ring-[#135bec] text-gray-900 dark:text-slate-100 placeholder-gray-500 dark:placeholder-slate-400" 
                  placeholder={t('performance.filterByOperationName')} 
                  type="text"
                  value={operationNameFilter}
                  onChange={(e) => setOperationNameFilter(e.target.value)}
                />
              </div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm text-[#616f89] dark:text-slate-400">
              <thead className="bg-gray-50 dark:bg-slate-800 text-xs uppercase font-semibold text-[#111318] dark:text-slate-100">
                <tr>
                  <th className="px-6 py-3">{tCommon('date')}</th>
                  <th className="px-6 py-3">{t('performance.operationType')}</th>
                  <th className="px-6 py-3">{tCommon('status')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#e5e7eb] dark:divide-slate-600">
                {activityLog.data.length === 0 ? (
                  <tr>
                    <td colSpan="3" className="px-6 py-8 text-center text-[#616f89] dark:text-slate-400">
                      {t('performance.noSuccessfulOperations')}
                    </td>
                  </tr>
                ) : (
                  activityLog.data.map((imputation) => (
                    <tr key={imputation.id} className="bg-white dark:bg-slate-800 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-[#616f89] dark:text-slate-400">
                        {formatTimestamp(imputation.date_operation)}
                      </td>
                      <td className="px-6 py-4 text-[#616f89] dark:text-slate-400">
                        {imputation.operation ? (
                          <div>
                            <div className="font-medium text-[#111318] dark:text-slate-100">{imputation.operation.production_line_lp}</div>
                            <div className="text-xs text-[#616f89] dark:text-slate-400">{imputation.operation.activity}</div>
                          </div>
                        ) : '-'}
                      </td>
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2 py-1 text-xs font-medium text-green-700 dark:bg-green-900/40 dark:text-green-200">
                          <span className="h-1.5 w-1.5 rounded-full bg-green-600"></span>
                          {t('performance.completed')}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-[#e5e7eb] dark:border-slate-600 px-6 py-4">
            <div className="flex items-center gap-4">
              <div className="text-sm text-[#616f89] dark:text-slate-400">
                {activityLog.loading ? (
                  <span>{tCommon('loadingEllipsis')}</span>
                ) : activityLog.total > 0 ? (
                  <>
                    Showing <span className="font-medium text-[#111318] dark:text-slate-100">{(activityLogPage - 1) * activityLogPageSize + 1}</span> to <span className="font-medium text-[#111318] dark:text-slate-100">{Math.min(activityLogPage * activityLogPageSize, activityLog.total)}</span> of <span className="font-medium text-[#111318] dark:text-slate-100">{activityLog.total}</span> results
                  </>
                ) : (
                  <span>{tCommon('noResults')}</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-[#616f89] dark:text-slate-400">{tCommon('perPage')}</span>
                <div className="relative">
                  <select
                    value={activityLogPageSize}
                    onChange={(e) => {
                      setActivityLogPageSize(Number(e.target.value));
                      setActivityLogPage(1);
                    }}
                    className="appearance-none rounded border border-[#dbe0e6] dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-1.5 pr-8 text-sm text-[#111318] dark:text-slate-100 focus:border-[#135bec] focus:ring-1 focus:ring-[#135bec] outline-none"
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                  <span className="material-symbols-outlined absolute right-2 top-1/2 -translate-y-1/2 text-[#616f89] dark:text-slate-400 text-[16px] pointer-events-none">arrow_drop_down</span>
                </div>
              </div>
            </div>
            {Math.ceil(activityLog.total / activityLogPageSize) > 1 && (
              <div className="flex items-center gap-2">
                <button 
                  className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-[#616f89] dark:text-slate-400 hover:bg-gray-50 dark:hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed" 
                  disabled={activityLogPage === 1}
                  onClick={() => setActivityLogPage(prev => Math.max(1, prev - 1))}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>chevron_left</span>
                </button>
                <button 
                  className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-[#616f89] dark:text-slate-400 hover:bg-gray-50 dark:hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={activityLogPage >= Math.ceil(activityLog.total / activityLogPageSize)}
                  onClick={() => setActivityLogPage(prev => Math.min(Math.ceil(activityLog.total / activityLogPageSize), prev + 1))}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>chevron_right</span>
                </button>
              </div>
            )}
          </div>
        </section>

        {/* Footer */}
        {/* <footer className="mt-8 text-center text-xs text-[#616f89]">
          <p>© 2023 Enterprise Tracker Inc. All data is processed according to internal privacy regulations.</p>
        </footer> */}
      </div>
    </div>
  );
};

export default EmployeePerformanceComparisonPage;
